/**
 * useStreamingASR — Browser-native Web Speech API for voice input.
 *
 * Uses the built-in SpeechRecognition API (Chrome/Edge/Safari) to convert
 * speech to text entirely in the browser. No backend service required.
 */

import { useRef, useCallback, useEffect } from 'react';
import { useStore } from '../store';

// Browser compat — webkit prefix for Chrome/Safari
const SpeechRecognition =
  (globalThis as any).SpeechRecognition || (globalThis as any).webkitSpeechRecognition;

export function useStreamingASR() {
  const recognitionRef = useRef<any>(null);

  const asrState = useStore((s) => s.asrState);
  const asrPartialText = useStore((s) => s.asrPartialText);
  const setAsrState = useStore((s) => s.setAsrState);
  const setAsrPartialText = useStore((s) => s.setAsrPartialText);

  const stopASR = useCallback(() => {
    if (recognitionRef.current) {
      recognitionRef.current.stop();
      recognitionRef.current = null;
    }
    setAsrState('idle');
    setAsrPartialText('');
  }, [setAsrState, setAsrPartialText]);

  const startASR = useCallback(() => {
    if (!SpeechRecognition) {
      setAsrState('error');
      return;
    }
    if (recognitionRef.current) {
      stopASR();
    }

    const recognition = new SpeechRecognition();
    recognition.continuous = true;
    recognition.interimResults = true;
    recognition.lang = navigator.language || 'zh-CN';

    recognitionRef.current = recognition;
    setAsrState('connecting');
    setAsrPartialText('');

    recognition.onstart = () => {
      setAsrState('recording');
    };

    recognition.onresult = (event: any) => {
      let interim = '';
      let final = '';
      for (let i = event.resultIndex; i < event.results.length; i++) {
        const transcript = event.results[i][0].transcript;
        if (event.results[i].isFinal) {
          final += transcript;
        } else {
          interim += transcript;
        }
      }

      if (final) {
        // Commit final text by dispatching a custom event the InputBar listens to
        window.dispatchEvent(new CustomEvent('asr-final', { detail: final }));
      }
      setAsrPartialText(interim);
    };

    recognition.onerror = (event: any) => {
      console.error('Speech recognition error:', event.error);
      if (event.error === 'not-allowed' || event.error === 'service-not-available') {
        setAsrState('error');
        recognitionRef.current = null;
      }
      // 'no-speech' and 'aborted' are transient — keep listening
    };

    recognition.onend = () => {
      // If still supposed to be recording, restart (browser may auto-stop)
      if (recognitionRef.current === recognition) {
        try {
          recognition.start();
        } catch {
          setAsrState('idle');
          recognitionRef.current = null;
        }
      }
    };

    try {
      recognition.start();
    } catch (err) {
      console.error('Failed to start speech recognition:', err);
      setAsrState('error');
      recognitionRef.current = null;
    }
  }, [setAsrState, setAsrPartialText, stopASR]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (recognitionRef.current) {
        recognitionRef.current.stop();
        recognitionRef.current = null;
      }
    };
  }, []);

  return {
    startASR,
    stopASR,
    asrState,
    asrPartialText,
    supported: !!SpeechRecognition,
  };
}
